import React, { useEffect, useMemo, useState, useCallback } from "react";
import { AdminPanelPage } from "../AdminPanelPage";
import { useHistory, useParams } from "react-router-dom";
import { DashboardWithComponents, useDashboards } from "../../Contexts/DashboardsContext";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { DashboardComponentCard } from "./components/DashboardComponents/DashboardComponentCard";
import { BreadcrumbProps, Button, Icon, Tooltip, DashboardGrid, DashboardGridItem, DashboardGridLayout, useIsMobile } from "@aidkitorg/component-library";
import { DashboardComponentWithId } from "@aidkitorg/types/lib/admin-panel/dashboard";
import { useDashboardImageExport } from "./hooks/useDashboardImageExport";
import { useLocalizedStrings } from "../../Localization";

export const DashboardPage = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
  const { dashboard_id } = useParams<{ dashboard_id: string }>()

  const [dashboard, setDashboard] = useState<DashboardWithComponents>()
  const [components, setComponents] = useState<Array<DashboardComponentWithId>>([])

  const { imageRef, isGeneratingImage, handleDownloadImage } = useDashboardImageExport(dashboard_id)
  const { dashboardsById, fetchDashboardById, getDashboardById } = useDashboards()

  const isMobile = useIsMobile()

  const GRID_COLS = 6
  const GRID_WIDTH = 1200
  const GRID_ROW_HEIGHT = 160

  useAsyncEffect(async () => {
    await fetchDashboardById(dashboard_id)
  }, [])

  useEffect(() => {
    const dashboard = dashboardsById[dashboard_id]
    if (dashboard) {
      setDashboard(dashboard)
      setComponents(dashboard.components)
    }
  }, [getDashboardById, dashboard_id, dashboardsById])

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.dashboards,
      href: '/admin-panel/dashboards'
    },
    ...((!dashboard) ? [] : [{
      label: dashboard.title
    }])
  ]
  
  const pageActionButtons = [
    <Tooltip text={L.admin_panel.dashboards.save_as_img} asChild key="png">
      <Button 
        loading={isGeneratingImage}
        className="png-hide"
        size="sm"
        variant="secondary"
        onClick={() => handleDownloadImage(dashboard?.title || '')}
        icon={<Icon type="solid" name="PhotoIcon" role="decorative" />}
      />
    </Tooltip>
  ]

  if (!dashboard?.view_only) {
    pageActionButtons.push(
      <Button
        key="edit"
        className="png-hide"
        onClick={() => {
          history.push(`/admin-panel/dashboards/${dashboard_id}/edit`);
        }}
      >
        {L.admin_panel.dashboards.edit_dashboard}
      </Button>
    )
  }
  
  const sizeByKind: Record<string, { w: number, h: number, minW: number, minH: number }> = {
    'Hero Number': { w: 1, h: 1, minW: 1, minH: 1 },
    'Bar Chart':   { w: 4, h: 3, minW: 2, minH: 3 },
    'Pie Chart':   { w: 4, h: 3, minW: 2, minH: 3 },
    'Map':         { w: 5, h: 4, minW: 2, minH: 3 },
  };
  
  const getSizeForKind = (kind: string) => sizeByKind[kind] || { w: 2, h: 2 };
  
  const gridItems: Array<DashboardGridItem<DashboardComponentWithId>> = useMemo(() => {
    return components.map((component) => ({
      id: component.id,
      data: component,
      layout: component.style || {
        i: component.id,
        x: 0,
        y: 0,
        w: getSizeForKind(component.config.kind).w,
        h: getSizeForKind(component.config.kind).h,
        minW: getSizeForKind(component.config.kind).minW,
        minH: getSizeForKind(component.config.kind).minH,
        static: true,
      }
    }));
  }, [components]);

  const renderGridItemCallback = useCallback((item: DashboardGridItem<DashboardComponentWithId>, dimensions: { width: number; height: number }) => {
    return <DashboardComponentCard 
      id={item.data.id}
      component={item.data.config}
      width={dimensions.width}
      height={dimensions.height}
    />
  }, [])


  const dashboardEmptyState = (        
    <div className="flex-grow flex items-center justify-center h-64 text-gray-500">
      <div className="text-center">
        <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
        </svg>
        <div className="text-lg font-medium mb-2">{L.admin_panel.dashboards.empty_dashboard}</div>
        <div>{L.admin_panel.dashboards.empty_dashboard_msg}</div>
      </div>
    </div>
  )

  // Sort components for mobile single-column layout (left-to-right, top-to-bottom)
  const sortedComponentsForMobile = useMemo(() => {
    return [...gridItems].sort((a, b) => {
      const aY = a.layout?.y || 0;
      const bY = b.layout?.y || 0;
      const aX = a.layout?.x || 0;
      const bX = b.layout?.x || 0;
    
      if (aY !== bY) {
        return aY - bY; // Sort by row first
      }
      return aX - bX; // Then by column
    })}, [gridItems])

  return <AdminPanelPage 
    ref={imageRef}
    title={dashboard?.title || ''}
    description={dashboard?.description}
    pageActionButtons={pageActionButtons}
    breadcrumbs={breadcrumbs}
    keyboardShortcuts={[
      { key: 'E', description: L.admin_panel.dashboards.edit_dashboard_keyboard_shortcut, modifier: 'meta', callback: () => { history.push(`/admin-panel/dashboards/${dashboard_id}/edit`) } },
      { key: 'R', description: L.admin_panel.dashboards.refresh_dashboard, modifier: 'meta', },
      { key: 'B', description: L.admin_panel.reports.toggle_sidebar, modifier: 'meta' },
      { key: 'K', description: L.admin_panel.command_palette.keyboard_shortcut, modifier: 'meta' },
      {
        displayKey: '↑', key: 'ArrowUp', description: L.admin_panel.dashboards.go_to_dashboards, modifier: 'meta', callback: () => { history.push('/admin-panel/dashboards') }
      },
    ]}
  >
    {isMobile ? (
      <div className="flex-grow">
        {components.length === 0 ? (
          dashboardEmptyState
        ) : (
          <div className="space-y-2 p-2">
            {sortedComponentsForMobile.map((item) => (
              <div key={item.id} className="w-full">
                <DashboardComponentCard 
                  id={item.data.id}
                  component={item.data.config}
                  width={window.innerWidth - 32} // Full width minus padding
                  height={GRID_ROW_HEIGHT * (item.layout?.h || 2)} // Maintain proportional height
                />
              </div>
            ))}
          </div>
        )}
      </div>
    ) : (
      <div className="flex-grow">
        <DashboardGrid
          items={gridItems}
          renderItem={renderGridItemCallback}
          rowHeight={GRID_ROW_HEIGHT}
          isDraggable={false}
          isResizable={false}
          defaultGridCols={GRID_COLS}
          defaultGridWidth={GRID_WIDTH}
          emptyState={dashboardEmptyState}
        />
      </div>
    )}
  </AdminPanelPage>
}